import { Search, X } from "lucide-react"

import { Button, Input } from "@aix/ui"

// Match the schema enums
const GENRES = [
  { value: "bollywood", label: "Bollywood" },
  { value: "hip_hop", label: "Hip Hop" },
  { value: "pop", label: "Pop" },
  { value: "edm", label: "EDM" },
  { value: "latin", label: "Latin" },
  { value: "rnb", label: "R&B" },
  { value: "rock", label: "Rock" },
  { value: "country", label: "Country" },
  { value: "afrobeats", label: "Afrobeats" },
  { value: "kpop", label: "K-Pop" },
  { value: "reggaeton", label: "Reggaeton" },
  { value: "house", label: "House" },
  { value: "trap", label: "Trap" },
  { value: "drill", label: "Drill" },
] as const

const STATUSES = [
  { value: "uploading", label: "Uploading", color: "bg-yellow-500" },
  { value: "uploaded", label: "Uploaded", color: "bg-blue-500" },
  { value: "separating", label: "Separating", color: "bg-purple-500" },
  { value: "analyzing", label: "Analyzing", color: "bg-indigo-500" },
  { value: "ready", label: "Ready", color: "bg-green-500" },
  { value: "error", label: "Error", color: "bg-red-500" },
] as const

export interface FilterState {
  status?: string
  genre?: string
  bpmMin?: number
  bpmMax?: number
  search: string
}

interface SongFiltersProps {
  filters: FilterState
  onFiltersChange: (filters: FilterState) => void
}

export function SongFilters({ filters, onFiltersChange }: SongFiltersProps) {
  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const clearFilters = () => {
    onFiltersChange({
      status: undefined,
      genre: undefined,
      bpmMin: undefined,
      bpmMax: undefined,
      search: "",
    })
  }

  const hasActiveFilters = filters.status || filters.genre || filters.bpmMin || filters.bpmMax || filters.search

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by title or artist..."
          value={filters.search}
          onChange={(e) => updateFilter("search", e.target.value)}
          className="pl-10 text-sm"
        />
      </div>

      {/* Filter Pills - scrollable on mobile */}
      <div className="flex flex-wrap gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {/* Status Filter */}
        <select
          value={filters.status || ""}
          onChange={(e) => updateFilter("status", e.target.value || undefined)}
          className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-border rounded-md bg-background hover:bg-accent cursor-pointer flex-shrink-0"
        >
          <option value="">All Status</option>
          {STATUSES.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>

        {/* Genre Filter */}
        <select
          value={filters.genre || ""}
          onChange={(e) => updateFilter("genre", e.target.value || undefined)}
          className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm border border-border rounded-md bg-background hover:bg-accent cursor-pointer flex-shrink-0"
        >
          <option value="">All Genres</option>
          {GENRES.map((genre) => (
            <option key={genre.value} value={genre.value}>
              {genre.label}
            </option>
          ))}
        </select>

        {/* BPM Range */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <Input
            type="number"
            placeholder="Min"
            value={filters.bpmMin || ""}
            onChange={(e) => updateFilter("bpmMin", e.target.value ? parseInt(e.target.value) : undefined)}
            className="w-16 sm:w-24 h-8 text-xs sm:text-sm"
          />
          <span className="text-muted-foreground text-xs">-</span>
          <Input
            type="number"
            placeholder="Max"
            value={filters.bpmMax || ""}
            onChange={(e) => updateFilter("bpmMax", e.target.value ? parseInt(e.target.value) : undefined)}
            className="w-16 sm:w-24 h-8 text-xs sm:text-sm"
          />
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="flex-shrink-0 h-8">
            <X className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            <span className="text-xs sm:text-sm">Clear</span>
          </Button>
        )}
      </div>
    </div>
  )
}
